import RedirectLink from '@Components/RedirectLink';
import TableTemplate, { TableConfig } from '@Dashboard/Charts/TableTemplate';
import { generateRedirectUrlToLog } from '@Log/shared';
import { useDashboardServicesStore } from '@Models/useDashboardServicesModel';
import { useUtcOffsetStore } from '@Models/useUtcOffsetModel';
import { getServiceNamesByValues } from '@Utils/service';
import { getUtcTime } from '@Utils/time';
import { useColorMode, useColorStyle, Box, Text } from '@tonic-one/react';
import { useTranslation } from 'react-i18next';
import { MAIL_SERVICES, NOT_MAIL_SERVICES } from '../utils';
import { ServiceNameEnum } from '@Types/index';
import { LogTypeEnum, SecurityFilterName } from '@Types/log';
import { useMemo } from 'react';

const SpamGraymailSenderTable = (props: { insightsData: any; day: string }) => {
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  const { t } = useTranslation();
  const timeModel = useUtcOffsetStore();
  const servicesModel = useDashboardServicesStore();
  const services = servicesModel.enabledServices;

  const config: TableConfig[] = [
    { width: '20%', grow: 2, align: 'left', tooltip: true },
    { width: '15%', grow: 1, align: 'right' },
    { width: '15%', grow: 1, align: 'right' },
    { width: '15%', grow: 1, align: 'right' },
    { width: '20%', grow: 2, align: 'left' },
  ];
  const header = [
    t('riskUser.topUser.user'),
    t('riskUser.topUser.totalAmount'),
    t('riskUser.topUser.graymail'),
    t('riskUser.topUser.otherSpam'),
    t('riskUser.topUser.sent'),
  ];

  const tableData = useMemo(() => {
    const getTotal = (x: any) => {
      return (x.spam_graymail || 0) + (x.spam_other || 0);
    };

    const data = props.insightsData?.SuspiciousTopSenders?.[props.day] || [];
    data.sort((a: any, b: any) => {
      return getTotal(b) - getTotal(a);
    });

    return data.map((x: any) => {
      return [
        x.key || '',
        renderSpamLink(
          'all',
          getTotal(x),
          x.key || '',
          !!x.is_email_sender,
          props.day,
          services
        ),
        renderSpamLink(
          'graymail',
          x.spam_graymail || 0,
          x.key || '',
          !!x.is_email_sender,
          props.day,
          services
        ),
        renderSpamLink(
          'otherspam',
          x.spam_other || 0,
          x.key || '',
          !!x.is_email_sender,
          props.day,
          services
        ),
        x.last_detected
          ? getUtcTime(
              x.last_detected,
              timeModel.offset,
              t('common.momentDateFormat')
            )
          : '',
      ];
    });
  }, [props.insightsData, props.day, services, timeModel.offset, t]);

  return (
    <Box>
      <Box
        mb="24px"
        fontSize="18px"
        fontWeight="600"
        lineHeight="24px"
        color={colorStyle.color.primary}
      >
        {t('riskUser.topUser.spamSender')}
      </Box>
      <TableTemplate
        height="220px"
        config={config}
        header={header}
        data={tableData}
      />
    </Box>
  );
};

function renderSpamLink(
  type: 'all' | 'graymail' | 'otherspam',
  count: number,
  key: string,
  isEmailSender: boolean,
  day: string,
  services: ServiceNameEnum[]
) {
  if (count === 0) {
    return 0;
  }
  return (
    <RedirectLink
      href={`logs?${generateRedirectUrlToLog(
        [
          {
            field: 'spamCategory',
            values: [
              type === 'all' || type === 'graymail' ? 'GrayEmail Bulk' : '',
              type === 'all' || type === 'graymail' ? 'GrayEmail Forum' : '',
              type === 'all' || type === 'graymail'
                ? 'GrayEmail Social Network'
                : '',
              type === 'all' || type === 'graymail' ? 'GrayEmail MML' : '',
              type === 'all' || type === 'otherspam' ? 'Spam Others' : '',
            ].filter((x) => !!x),
            type: 'terms',
          },
          isEmailSender
            ? {
                field: 'msgFrom',
                values: [key],
                type: 'terms',
              }
            : {
                field: 'violator',
                values: [key],
                type: 'terms',
              },
          // {
          //   field: 'mailDirection',
          //   values: ['out'],
          //   type: 'terms',
          // },
          {
            field: 'securityFilterName',
            values: [SecurityFilterName.AdvancedSpamProtection],
            type: 'terms',
          },
          isEmailSender
            ? {
                field: 'scanSource',
                values: getServiceNamesByValues(
                  services.filter((x) => MAIL_SERVICES.has(x))
                ),
                type: 'terms',
              }
            : {
                field: 'scanSource',
                values: getServiceNamesByValues(
                  services.filter((x) => NOT_MAIL_SERVICES.has(x))
                ),
                type: 'terms',
              },
        ],
        day,
        LogTypeEnum.DETECTION
      )}`}
    >
      <Text>{count}</Text>
    </RedirectLink>
  );
}

export default SpamGraymailSenderTable;
